import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { MoreFeaturedNews as MoreFeaturedNewsComponent, MoreFeaturedNewsItem }
from '@fuww/components/src/Articles/Sections/Featured/MoreFeaturedNews';
import Border from '@fuww/components/src/Articles/Sections/Border';
import breakpoints from '@fuww/components/src/Theme/breakpoints';
import CardWithLink from '../../../CardWithLink';
import withQuery from '../../../../lib/withQuery';
import getArticlesQuery from '../../../../lib/queries/news/getArticlesQuery';
import getArticlesField from '../../../../lib/queries/news/getArticlesField';
import featured from '../../../../lib/queries/news/variables/featured';
import { featuredCategoryIds } from '../../../../lib/mainCategoryIds';
import SiteContext from '../../../SiteContext';
import messages from '../../../../lib/messages';
import useNoCacheOnServerFetchPolicy
from '../../../../lib/useNoCacheOnServerFetchPolicy';

const MoreNewsCard = ({
  article,
  variables,
  showCreatedAt,
  indicateFeatured,
}) => (
  <CardWithLink
    newsArticle={article}
    variables={variables}
    sizes={`
      (min-width: 1440px) 318px,
      (min-width: ${breakpoints.lg}px) 23vw,
      (min-width: ${breakpoints.sm}px) 47vw,
      100vw
    `}
    showCreatedAt={showCreatedAt}
    indicateFeatured={indicateFeatured}
    withPageMargins
  />
);

MoreNewsCard.propTypes = {
  article: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}).isRequired,
  showCreatedAt: PropTypes.bool,
  indicateFeatured: PropTypes.bool,
};

MoreNewsCard.defaultProps = {
  showCreatedAt: false,
  indicateFeatured: true,
};

const MoreFeaturedArticles = ({
  data,
  variables,
  articlesField,
  showCreatedAt,
}) => {
  const articles = data[articlesField];
  return (
    <>
      {articles.map((article) => (
        <MoreFeaturedNewsItem key={article.id}>
          <MoreNewsCard
            article={article}
            variables={variables}
            showCreatedAt={showCreatedAt}
            indicateFeatured={false}
          />
        </MoreFeaturedNewsItem>
      ))}
    </>
  );
};

MoreFeaturedArticles.propTypes = {
  data: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}).isRequired,
  articlesField: PropTypes.string.isRequired,
  showCreatedAt: PropTypes.bool,
};

MoreFeaturedArticles.defaultProps = {
  showCreatedAt: false,
};

const MoreFeaturedArticlesWithQuery = withQuery(MoreFeaturedArticles);
const errorMessage = 'Error loading news articles...';

const MoreFeaturedNews = ({ variables, showCreatedAt, notInIds }) => {
  const intl = useIntl();
  const { prefix, hasLocalNews, newsboard } = useContext(SiteContext);
  const fetchPolicy = useNoCacheOnServerFetchPolicy();

  const field = getArticlesField(prefix, hasLocalNews);
  const query = getArticlesQuery(field);
  const featuredVariables = {
    ...featured,
    newsboards: [newsboard],
    notInIds,
    categoryIds: featuredCategoryIds,
    ...variables,
  };

  return (
    <>
      <Border />
      <MoreFeaturedNewsComponent
        categoryHeading={intl.formatMessage(messages.featured)}
        moreFeaturedNewsArticles={(
          <MoreFeaturedArticlesWithQuery
            query={query}
            variables={featuredVariables}
            errorMessage={errorMessage}
            articlesField={field}
            showCreatedAt={showCreatedAt}
            fetchPolicy={fetchPolicy}
          />
        )}
      />
    </>
  );
};

MoreFeaturedNews.propTypes = {
  variables: PropTypes.shape({}),
  showCreatedAt: PropTypes.bool,
  notInIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

MoreFeaturedNews.defaultProps = {
  variables: null,
  showCreatedAt: false,
};

export default MoreFeaturedNews;
